import type { TeamsUsersResponse } from "../../../queries/useTeamsUsersQuery";

type User = TeamsUsersResponse["users"][number];
type Team = TeamsUsersResponse["teams"][number];

export type SortDirection = "asc" | "desc";
export type UserSortField = "name" | "role" | "teams" | "lastLogin";
export type TeamSortField = "team" | "lead" | "members" | "description";
export type ColumnSort<Field extends string> = {
  field: Field;
  dir: SortDirection;
};

type SortKey = string | number | null;

/** The sort for a stored field and direction; a direction alone is ignored. */
export function resolveColumnSort<Field extends string>(
  field: Field | undefined,
  dir: SortDirection | undefined,
): ColumnSort<Field> | undefined {
  return field ? { field, dir: dir ?? "asc" } : undefined;
}

/** A new column starts ascending; the active column flips direction. */
export function nextColumnSort<Field extends string>(
  current: ColumnSort<Field> | undefined,
  field: Field,
): ColumnSort<Field> {
  return {
    field,
    dir: current?.field === field && current.dir === "asc" ? "desc" : "asc",
  };
}

function compareKeys(left: string | number, right: string | number) {
  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }
  return String(left).localeCompare(String(right), undefined, {
    sensitivity: "base",
  });
}

/**
 * Orders rows by a key. Rows without a key always go last, whichever
 * direction is active, and ties keep their original order. Without a sort the
 * input is returned untouched.
 */
function sortByKey<Row>(
  rows: Row[],
  keyOf: (row: Row) => SortKey,
  dir: SortDirection | undefined,
): Row[] {
  if (!dir) return rows;
  const direction = dir === "desc" ? -1 : 1;
  return rows
    .map((row, index) => ({ row, index, key: keyOf(row) }))
    .sort((left, right) => {
      if (left.key === null && right.key !== null) return 1;
      if (left.key !== null && right.key === null) return -1;
      if (left.key !== null && right.key !== null) {
        const result = compareKeys(left.key, right.key);
        if (result !== 0) return result * direction;
      }
      return left.index - right.index;
    })
    .map(({ row }) => row);
}

function textKey(value: string | null | undefined): SortKey {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export function teamLead(team: Team) {
  return team.members.find((member) => member.isTeamLead) ?? team.members[0];
}

function userKey(user: User, field: UserSortField): SortKey {
  switch (field) {
    case "name":
      return textKey(user.name);
    case "role":
      return textKey(user.role);
    case "teams":
      return textKey(user.teams.join(", "));
    case "lastLogin": {
      if (!user.lastLogin) return null;
      const time = Date.parse(user.lastLogin);
      return Number.isNaN(time) ? null : time;
    }
  }
}

function teamKey(team: Team, field: TeamSortField): SortKey {
  switch (field) {
    case "team":
      return textKey(team.name);
    case "lead":
      return textKey(teamLead(team)?.name);
    case "members":
      return team.memberCount;
    case "description":
      return textKey(team.description);
  }
}

export function sortUsers(
  users: User[],
  sort?: ColumnSort<UserSortField>,
): User[] {
  return sortByKey(
    users,
    (user) => userKey(user, sort?.field ?? "name"),
    sort?.dir,
  );
}

export function sortTeams(
  teams: Team[],
  sort?: ColumnSort<TeamSortField>,
): Team[] {
  return sortByKey(
    teams,
    (team) => teamKey(team, sort?.field ?? "team"),
    sort?.dir,
  );
}
